import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// F6 · Slice de plantillas de horario compartidas.
// Endpoint base: /api/scheduleTemplate

const initialState = {
  isLoading: false,
  error: null,
  list: [],
  byId: {},                    // { [templateId]: DtoScheduleTemplateDetail }
  assignmentsByTemplate: {},   // { [templateId]: DtoTemplateAssignmentGet[] }
  historyByEmployee: {},       // { [employeeId]: DtoEmployeeTemplateHistory }
  migrationReport: null,
};

const slice = createSlice({
  name: "scheduleTemplates",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    stopLoading(state) { state.isLoading = false; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },

    setList(state, action) {
      state.list = action.payload || [];
      state.isLoading = false;
    },
    setDetail(state, action) {
      const detail = action.payload;
      if (detail?.id) state.byId[detail.id] = detail;
      state.isLoading = false;
    },
    upsertInList(state, action) {
      const t = action.payload;
      const idx = state.list.findIndex((x) => x.id === t.id);
      if (idx >= 0) state.list[idx] = { ...state.list[idx], ...t };
      else state.list.unshift(t);
    },
    removeFromList(state, action) {
      const id = action.payload;
      state.list = state.list.filter((x) => x.id !== id);
      delete state.byId[id];
      delete state.assignmentsByTemplate[id];
    },
    setAssignments(state, action) {
      const { templateId, assignments } = action.payload;
      state.assignmentsByTemplate[templateId] = assignments || [];
    },
    setEmployeeHistory(state, action) {
      const h = action.payload;
      if (h?.employeeId != null) state.historyByEmployee[h.employeeId] = h;
    },
    setMigrationReport(state, action) {
      state.migrationReport = action.payload;
    },
  },
});

export default slice.reducer;
export const {
  setList,
  setDetail,
  upsertInList,
  removeFromList,
  setAssignments,
  setEmployeeHistory,
  setMigrationReport,
} = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getTemplates() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get("/scheduleTemplate");
      dispatch(slice.actions.setList(res.data));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error"));
    }
  };
}

export function getTemplate(templateId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(`/scheduleTemplate/${templateId}`);
      dispatch(slice.actions.setDetail(res.data));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error"));
    }
  };
}

export function createTemplate(dto) {
  return async (dispatch) => {
    const res = await axios.post("/scheduleTemplate", dto);
    dispatch(slice.actions.upsertInList(res.data));
    return res.data;
  };
}

export function updateTemplate(templateId, dto) {
  return async (dispatch) => {
    const res = await axios.put(`/scheduleTemplate/${templateId}`, dto);
    dispatch(slice.actions.upsertInList(res.data));
    return res.data;
  };
}

export function deleteTemplate(templateId) {
  return async (dispatch) => {
    await axios.delete(`/scheduleTemplate/${templateId}`);
    dispatch(slice.actions.removeFromList(templateId));
  };
}

export function cloneTemplate(templateId, newName) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/${templateId}/clone`, { newName });
    dispatch(slice.actions.upsertInList(res.data));
    return res.data;
  };
}

/**
 * 2026-09-28 · Detalle de impacto de editar la plantilla — cuántos
 * empleados con assignment vigente y WSI futuros se regenerarán. El
 * editor lo consulta antes de commit para preguntar "aplicar a futuro"
 * vs "clonar en plantilla nueva". Devuelve `{ templateId,
 * activeAssignmentsCount, futureWorkItemsCount, employeeNames[] }`.
 */
export async function getTemplateUsageDetail(templateId) {
  const res = await axios.get(`/scheduleTemplate/${templateId}/usage-detail`);
  return res.data;
}

/**
 * F6 · Guardado atómico: crea o edita plantilla + slots + rotación en 1 request.
 * dto = {
 *   id?,           // null/0 = crear
 *   name, description, color, isActive,
 *   slots: [{id?, dayOfWeek, startTime, endTime, breakDuration?, rotationWeekIndex?}],
 *   deletedSlotIds: [],
 *   rotation?: { id?, name, totalWeeks, anchorDate, weeks: [{weekIndex, label}] },
 *   removeRotation?: bool,
 *   propagateAfterSave?: bool
 * }
 */
export function saveTemplateFull(dto) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/full`, dto);
    dispatch(slice.actions.setDetail(res.data));
    dispatch(slice.actions.upsertInList(res.data));
    return res.data;
  };
}

/**
 * F6 · Guardar los turnos de una plantilla en bulk.
 * dto = { upserts: [{id?, dayOfWeek, startTime, endTime, breakDuration?, rotationWeekId?}], deletedIds: [], propagateAfterSave?: bool }
 */
export function saveTemplateSlots(templateId, dto) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/${templateId}/slots`, dto);
    dispatch(slice.actions.setDetail(res.data));
    // Refrescar la lista para actualizar contadores (slotCount, hasRotation).
    await dispatch(getTemplates());
    return res.data;
  };
}

export function getAssignments(templateId) {
  return async (dispatch) => {
    const res = await axios.get(`/scheduleTemplate/${templateId}/assignments`);
    dispatch(slice.actions.setAssignments({ templateId, assignments: res.data }));
    return res.data;
  };
}

/**
 * dto = { employeeIds: number[], validFrom: ISOstring, validTo?: ISOstring, replaceActive?: bool, notes?: string }
 */
export function assignBulk(templateId, dto) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/${templateId}/assignments`, dto);
    // Refrescamos la lista para actualizar contadores.
    await dispatch(getTemplates());
    await dispatch(getAssignments(templateId));
    return res.data;
  };
}

export function updateAssignment(assignmentId, dto) {
  return async (dispatch, getState) => {
    const res = await axios.put(`/scheduleTemplate/assignments/${assignmentId}`, dto);
    const templateId = res.data?.templateId;
    if (templateId) await dispatch(getAssignments(templateId));
    return res.data;
  };
}

export function closeAssignment(assignmentId, validTo) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/assignments/${assignmentId}/close`, { validTo });
    const templateId = res.data?.templateId;
    if (templateId) await dispatch(getAssignments(templateId));
    return res.data;
  };
}

export function deleteAssignment(assignmentId, templateId) {
  return async (dispatch) => {
    await axios.delete(`/scheduleTemplate/assignments/${assignmentId}`);
    if (templateId) await dispatch(getAssignments(templateId));
    await dispatch(getTemplates());
  };
}

export function propagateTemplate(templateId) {
  return async () => {
    const res = await axios.post(`/scheduleTemplate/${templateId}/propagate`);
    return res.data;
  };
}

export function getEmployeeHistory(employeeId) {
  return async (dispatch) => {
    const res = await axios.get(`/scheduleTemplate/employees/${employeeId}/history`);
    dispatch(slice.actions.setEmployeeHistory(res.data));
    return res.data;
  };
}

// Migración F6 Fase 2.
export function migrateTemplates(dryRun = true) {
  return async (dispatch) => {
    const res = await axios.post(`/scheduleTemplate/migrate?dryRun=${dryRun}`);
    dispatch(slice.actions.setMigrationReport(res.data));
    return res.data;
  };
}
